import { useEffect, useRef, type FormEvent, type ReactNode } from 'react'
import { AlertCircle, ArrowLeft, ArrowRight, History, Loader2, Pencil } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { PropertyList, PropertyRow } from '@/components/ui/property-list'
import { Stepper } from '@/components/ui/stepper'
import type { FormDraft } from './useFormDraft'
import type { FormWizardState } from './useFormWizard'

const savedAtLabel = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * A long create form split into steps: the steps on the left (a progress bar
 * on phones), one step at a time in the middle, and Back / Next always within
 * reach at the bottom of the screen. Enter moves to the next step; on the
 * last step it sends the form.
 */
export function FormWizard<Id extends string>({
  wizard,
  submitLabel,
  busy = false,
  busyLabel = 'Saving',
  error,
  errorTitle = 'Not saved',
  onSubmit,
  draft,
  draftLabel = 'answers',
  children,
}: {
  wizard: FormWizardState<Id>
  /** Keeps answers in this browser until the record is created. */
  draft?: FormDraft
  /** What the draft holds, as in "You have unsaved inquiry answers". */
  draftLabel?: string
  submitLabel: string
  busy?: boolean
  busyLabel?: string
  error?: string | null
  errorTitle?: string
  /** Called on the last step, once every step is valid. */
  onSubmit: () => void
  children: ReactNode
}) {
  const containerRef = useRef<HTMLFormElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)

  // The draft remembers which step someone was on, and reopens it on restore.
  const stepId = wizard.step.id
  useEffect(() => {
    draft?.track('__step', stepId)
  })
  const restoredStep = draft?.restoredStep ?? null
  const reopened = useRef(false)
  useEffect(() => {
    if (!restoredStep || reopened.current) return
    reopened.current = true
    const position = wizard.steps.findIndex((step) => step.id === restoredStep)
    if (position > 0) wizard.goTo(position)
    // Once, after the restored answers are in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredStep])

  const handledFocus = useRef(0)
  useEffect(() => {
    const request = wizard.focus
    if (request.count === handledFocus.current) return
    // Wait until the requested step is on screen and its fields are editable again.
    if (request.step !== wizard.step.id || busy) return
    handledFocus.current = request.count
    // The first invalid field in reading order, whatever order the checks ran in.
    const field = request.targets
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))[0]
    if (field) {
      field.focus()
      field.scrollIntoView?.({ block: 'center' })
      return
    }
    headingRef.current?.focus({ preventScroll: true })
    containerRef.current?.scrollIntoView?.({ block: 'start' })
  }, [wizard.focus, wizard.step.id, busy])

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (!wizard.isLast) {
      wizard.next()
      return
    }
    if (wizard.validateAll()) onSubmit()
  }

  const errorCount = Object.keys(wizard.errors).length

  return (
    <form
      ref={containerRef}
      noValidate
      onSubmit={handleSubmit}
      // Fills the screen, so the Back / Next bar sits at the bottom even on short steps.
      className="flex min-h-[calc(100svh-14rem)] scroll-mt-4 flex-col gap-6"
      aria-busy={busy || undefined}
    >
      <div className="grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-start lg:gap-6">
        <Stepper
          data-tour="wizard-steps"
          className="lg:sticky lg:top-4"
          steps={wizard.steps}
          currentIndex={wizard.index}
          stateOf={wizard.stateOf}
          onSelect={wizard.goTo}
        />
        <Card className="min-w-0">
          <CardHeader>
            <h2
              ref={headingRef}
              id="wizard-step-heading"
              tabIndex={-1}
              className="text-lg font-semibold leading-none outline-none"
            >
              {wizard.step.title}
            </h2>
            <CardDescription>{wizard.step.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {draft?.offeredAt && (
              <Alert role="status">
                <History />
                <AlertTitle>You have unsaved {draftLabel}</AlertTitle>
                <AlertDescription>
                  <p>
                    From {savedAtLabel(draft.offeredAt)}, kept in this browser. Files you had chosen
                    need choosing again.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button type="button" size="sm" onClick={draft.restore}>
                      Restore
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={draft.discard}>
                      Start over
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertTitle>{errorTitle}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {errorCount > 1 && (
              <p role="status" className="text-sm text-destructive">
                {errorCount} things need attention before you can continue.
              </p>
            )}
            {children}
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 z-10 -mx-3 mt-auto -mb-3 border-t bg-background/95 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:-mx-6 md:-mb-6 md:px-6">
        <div className="flex items-center justify-between gap-3 lg:pl-[calc(13rem+1.5rem)]">
          <Button
            type="button"
            variant="outline"
            onClick={wizard.back}
            disabled={wizard.isFirst || busy}
          >
            <ArrowLeft />
            Back
          </Button>
          {wizard.isLast ? (
            <Button type="submit" variant="brand" disabled={busy} data-tour="wizard-submit">
              {busy && <Loader2 className="animate-spin" />}
              {busy ? busyLabel : submitLabel}
            </Button>
          ) : (
            <Button type="submit" disabled={busy} data-tour="wizard-next">
              <span>
                Next
                {wizard.nextStep && (
                  <span className="hidden sm:inline">: {wizard.nextStep.title}</span>
                )}
              </span>
              <ArrowRight />
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}

/** One step's answers on the review step, with a way back to change them. */
export function ReviewSection({
  title,
  onEdit,
  children,
}: {
  title: string
  onEdit: () => void
  children: ReactNode
}) {
  return (
    <section aria-label={title} className="rounded-md border">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
          <Pencil />
          Edit<span className="sr-only"> {title}</span>
        </Button>
      </div>
      <PropertyList className="border-b-0">{children}</PropertyList>
    </section>
  )
}

/** A reviewed answer; an empty one reads "Not set" rather than a blank. */
export function ReviewRow({ label, children }: { label: string; children?: ReactNode }) {
  const empty =
    children === null ||
    children === undefined ||
    children === '' ||
    (Array.isArray(children) && children.length === 0)
  return (
    <PropertyRow label={label}>
      {empty ? <span className="text-muted-foreground">Not set</span> : children}
    </PropertyRow>
  )
}
